import * as React from 'react';
import Typesense from "typesense";

export function useFacetBootstrap(
  {
    adapter,
    facets,
    index,
    filters,
    filterBy,
    groupByFields,
  }) {
  const [loading, setLoading] = React.useState(true);
  const [initialUiState, setInitialUiState] = React.useState(null);
  const [facetCounts, setFacetCounts] = React.useState(null);
  const [finalFilterBy, setFinalFilterBy] = React.useState(null);

  const cancelledRef = React.useRef(false);

  React.useEffect(() => {
    if (!index || !filters || filters.length === 0) {
      setLoading(false);
      return;
    }

    cancelledRef.current = false;

    async function run() {
      setLoading(true);

      try {
        const result = await buildAutoFilterState({
          adapter,
          facets,
          index,
          filters,
          filterBy,
          groupByFields,
        });

        if (!result || cancelledRef.current) return;

        const baseState = {
          refinementList: {},
          menu: {},
        };

        for (const key of Object.keys(result.uiState)) {
          const filterConfig = filters.find(f => f.attribute === key);
          if (!filterConfig) continue;

          if (filterConfig.kind === 'radio') {
            baseState.menu[key] = result.uiState[key][0];
          }
        }

        const indexState = {};
        if (Object.keys(baseState.refinementList).length) {
          indexState.refinementList = baseState.refinementList;
        }
        if (Object.keys(baseState.menu).length) {
          indexState.menu = baseState.menu;
        }

        setInitialUiState({[index]: indexState});
        setFacetCounts(result.facetCounts);
        setFinalFilterBy(result.finalFilterBy);
      } finally {
        if (!cancelledRef.current) {
          setLoading(false);
        }
      }
    }

    run();

    return () => {
      cancelledRef.current = true;
    };
  }, [
    adapter,
    facets,
    index,
    filters,
    filterBy,
    groupByFields,
  ]);

  return {
    loading,
    initialUiState,
    facetCounts,
    finalFilterBy,
  };
}

async function buildAutoFilterState({adapter, facets, index, filters, filterBy, groupByFields}) {
  const searchParameters = {
    q: "*",
    query_by: "*",
    facet_by: facets,
    max_facet_values: 50,
    filter_by: filterBy,
  };

  const serverConfig = adapter.configuration.server;

  const client = new Typesense.Client({
    nodes: [...serverConfig.nodes],
    apiKey: serverConfig.apiKey,
    connectionTimeoutSeconds: 2,
  });

  let uiState = {};
  let result = await client
    .collections(index)
    .documents()
    .search(searchParameters);

  if (!result) {
    return;
  }

  // Capture initial facet counts BEFORE applying any filters
  const initialFacetCounts = result.facet_counts;

  for (const filter of filters) {
    const facet = getFacet(result, filter.attribute);
    if (!facet || facet.counts.length === 0) {
      continue;
    }
    const sorted = [...facet.counts].sort((a, b) => a.value.localeCompare(b.value));
    const selectedValue = sorted[0].value;

    uiState[filter.attribute] = [selectedValue];

    result = await client
      .collections(index)
      .documents()
      .search({
        q: "*",
        query_by: "*",
        facet_by: facets,
        max_facet_values: 50,
        filter_by: buildFilterBy(filterBy, uiState),
      });
  }

  return {
    uiState,
    facetCounts: initialFacetCounts,  // Use initial facet counts, not final filtered ones
    lastResult: result,
    finalFilterBy: buildFilterBy(filterBy, uiState),
  };
}

function getFacet(result, attribute) {
  return result.facet_counts?.find(f => f.field_name === attribute);
}

function buildFilterBy(baseFilterBy, dynamicState) {
  const dynamicPart = Object.entries(dynamicState)
    .map(([attr, value]) => {
      if (Array.isArray(value)) {
        return `${attr}:=[${value.join(",")}]`;
      }
      return `${attr}:=${value}`;
    })
    .join(" && ");

  if (!baseFilterBy) return dynamicPart;
  if (!dynamicPart) return baseFilterBy;

  return `${baseFilterBy} && ${dynamicPart}`;
}